import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { User, Info, Grid, List, Heart, Users, Search } from 'lucide-react';
import { useGalleryStore } from '../../stores/galleryStore';
import { useAuthStore } from '../../stores/authStore';
import ImageWithFallback from '../common/ImageWithFallback';
import ArtistInfo from './ArtistInfo';
import PieceThumbnail from './PieceThumbnail';
import FavoritesView from './FavoritesView';
import type { Piece } from '@shared/types/firestore';

type PieceWithGalleryVariants = Piece & {
  gallery_thumbnail_path?: string | null;
  gallery_preview_path?: string | null;
};

const MAX_STAGGERED_PIECE_ANIMATIONS = 16;
const PIECE_ANIMATION_STAGGER_SECONDS = 0.015;

function getGalleryTileFallbackPath(piece: Piece): string {
  const pieceWithVariants = piece as PieceWithGalleryVariants;

  return (
    pieceWithVariants.gallery_thumbnail_path ||
    piece.image_full_path
  );
}

function shouldStaggerPieceAnimation(shouldAnimate: boolean, index: number): boolean {
  return shouldAnimate && index < MAX_STAGGERED_PIECE_ANIMATIONS;
}

function getPieceAnimationTransition(shouldAnimate: boolean, index: number) {
  return shouldStaggerPieceAnimation(shouldAnimate, index)
    ? { delay: index * PIECE_ANIMATION_STAGGER_SECONDS, duration: 0.18 }
    : { duration: 0 };
}

export default function ArtistSidebar() {
  const { user, galleryId } = useAuthStore();
  const { 
    artists, 
    selectedArtistId, 
    selectArtist, 
    selectPiece,
    getPiecesForArtist,
    searchQuery,
    setSearchQuery,
    showingFavorites,
    setShowingFavorites,
    loadFavorites,
    getFilteredPieces
  } = useGalleryStore();
  
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [showArtistInfo, setShowArtistInfo] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(20); // Start with first 20 artists
  const [animatePieces, setAnimatePieces] = useState<string | null>(null); // Track which artist's pieces to animate
  const prevViewMode = useRef(viewMode);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollAnchorRef = useRef<string | null>(null);
  const isRestoringScrollRef = useRef(false);
  const hideTitles = user?.hideTitles === true;
  const trimmedSearchQuery = searchQuery.trim();
  const isSearching = trimmedSearchQuery.length > 0;
  const filteredPieces = getFilteredPieces();
  const filteredPieceIds = useMemo(() => new Set(filteredPieces.map(piece => piece.id)), [filteredPieces]);
  const sidebarArtists = useMemo(() => {
    if (!isSearching) return artists;

    const matchingArtistIds = new Set(filteredPieces.map(piece => piece.artistID));
    return artists.filter(artist => artist.artistId && matchingArtistIds.has(artist.artistId));
  }, [artists, filteredPieces, isSearching]);

  // Load favorites when component mounts and user is available
  useEffect(() => {
    if (user?.userId) {
      loadFavorites(user.userId, galleryId ?? undefined);
    }
  }, [galleryId, user?.userId, loadFavorites]);

  // Trigger piece animation when artist selection or view mode changes
  useEffect(() => {
    if (selectedArtistId && (selectedArtistId !== animatePieces || viewMode !== prevViewMode.current)) {
      setAnimatePieces(selectedArtistId);
      prevViewMode.current = viewMode;
      const timer = setTimeout(() => setAnimatePieces(null), 50);
      return () => clearTimeout(timer);
    }
  }, [selectedArtistId, viewMode, animatePieces]);

  // Performance optimization: limit initial render for large artist lists
  const visibleArtists = useMemo(() => {
    return sidebarArtists.slice(0, visibleCount);
  }, [sidebarArtists, visibleCount]);

  // Calculate total piece count across all artists
  const totalPieceCount = useMemo(() => {
    return artists.reduce((total, artist) => {
      const pieces = getPiecesForArtist(artist.artistId!);
      return total + pieces.length;
    }, 0);
  }, [artists, getPiecesForArtist]);

  const hasMoreArtists = sidebarArtists.length > visibleCount;

  const loadMoreArtists = () => {
    setVisibleCount(prev => Math.min(prev + 20, sidebarArtists.length));
  };

  const handleArtistClick = (artistId: string) => {
    if (selectedArtistId === artistId) {
      selectArtist(null); // Deselect if clicking same artist
    } else {
      // Preserve current piece selection when changing artist
      selectArtist(artistId);
    }
  };

  const handlePieceClick = async (pieceId: string, _pieceImagePath: string) => {
    scrollAnchorRef.current = pieceId;
    selectPiece(pieceId);
  };

  const handleArtistInfoClick = (artistId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setShowArtistInfo(showArtistInfo === artistId ? null : artistId);
  };

  const updateScrollAnchor = () => {
    if (isRestoringScrollRef.current) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const anchors = Array.from(container.querySelectorAll<HTMLElement>('[data-sidebar-anchor]'));
    const containerTop = container.getBoundingClientRect().top;
    const topTolerance = 24;
    let nearestPastTop: { id: string; offset: number } | null = null;
    let nearestFutureTop: { id: string; offset: number } | null = null;

    anchors.forEach(anchor => {
      const id = anchor.dataset.sidebarAnchor;
      if (!id) return;

      const offset = anchor.getBoundingClientRect().top - containerTop;
      if (offset <= topTolerance) {
        if (!nearestPastTop || offset > nearestPastTop.offset) {
          nearestPastTop = { id, offset };
        }
        return;
      }

      if (!nearestFutureTop || offset < nearestFutureTop.offset) {
        nearestFutureTop = { id, offset };
      }
    });

    scrollAnchorRef.current = nearestPastTop?.id ?? nearestFutureTop?.id ?? null;
  };

  const restoreScrollAnchor = () => {
    const container = scrollContainerRef.current;
    const anchorId = scrollAnchorRef.current;
    if (!container || !anchorId) return;

    const target = container.querySelector<HTMLElement>(`[data-sidebar-anchor="${CSS.escape(anchorId)}"]`);
    if (!target) return;

    isRestoringScrollRef.current = true;
    target.scrollIntoView({ block: 'start' });
    window.requestAnimationFrame(() => {
      isRestoringScrollRef.current = false;
    });
  };

  useEffect(() => {
    if (showingFavorites) {
      updateScrollAnchor();
      return;
    }

    const frameId = window.requestAnimationFrame(restoreScrollAnchor);
    return () => window.cancelAnimationFrame(frameId);
  }, [showingFavorites, visibleArtists.length, selectedArtistId, searchQuery]);

  return (
    <div className="flex flex-col h-full">
      {/* ArtVR Logo - Mobile only */}
      <div className="md:hidden p-4 border-b border-gray-100 bg-white">
        <div className="mx-auto flex w-fit items-center justify-center rounded-xl bg-indigo-950 px-3 py-1.5 shadow-sm">
          <img 
            src="/artvr-logo.svg"
            alt="ArtVR" 
            className="h-8 w-auto"
          />
        </div>
      </div>

      {/* Header with Tabs */}
      <div className="border-b border-gray-200 bg-white">
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setShowingFavorites(false)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              !showingFavorites
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Artists</span>
              <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {artists.length}
              </span>
            </div>
          </button>
          <button
            onClick={() => setShowingFavorites(true)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              showingFavorites
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Heart className="w-4 h-4" />
              <span>Favorites</span>
            </div>
          </button>
        </div>

        {/* Mobile Search Bar */}
        <div className="md:hidden p-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search artworks or artists..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Controls - only show for Artists tab */}
        {!showingFavorites && (
          <div className="p-3 md:p-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-base md:text-lg font-semibold text-gray-900">
                  {isSearching ? 'Search Results' : 'Artists'}
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  {isSearching
                    ? `Showing ${Math.min(visibleCount, sidebarArtists.length)} of ${sidebarArtists.length} ${sidebarArtists.length === 1 ? 'artist' : 'artists'} • ${filteredPieces.length} ${filteredPieces.length === 1 ? 'artwork' : 'artworks'}`
                    : `Showing ${Math.min(visibleCount, artists.length)} of ${artists.length} ${artists.length === 1 ? 'artist' : 'artists'} • ${totalPieceCount} ${totalPieceCount === 1 ? 'piece' : 'pieces'} total`}
                </p>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600'}`}
                  title="List View"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Grid View"
                >
                  <Grid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content - either Artists List or Favorites */}
      {showingFavorites ? (
        <FavoritesView />
      ) : (
        <div ref={scrollContainerRef} onScroll={updateScrollAnchor} className="flex-1 overflow-y-auto">
        {visibleArtists.map((artist, index) => {
          const allArtistPieces = getPiecesForArtist(artist.artistId!);
          const artistPieces = isSearching
            ? allArtistPieces.filter(piece => piece.id && filteredPieceIds.has(piece.id))
            : allArtistPieces;
          const isSelected = selectedArtistId === artist.artistId;
          const isExpanded = isSearching || isSelected;
          const showingInfo = showArtistInfo === artist.artistId;

          return (
            <motion.div
              key={artist.artistId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="border-b border-gray-100"
              data-sidebar-anchor={`artist-${artist.artistId}`}
            >
              {/* Artist Header */}
              <div className={`p-3 md:p-4 hover:bg-gray-50 transition-colors ${
                isSelected ? 'bg-primary-50 border-r-2 border-primary-500' : ''
              }`}>
                <div className="flex items-center space-x-3">
                  {/* Artist Image */}
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
                    {artist.imagePath ? (
                      <ImageWithFallback
                        path={artist.imagePath}
                        alt={artist.displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-5 h-5 md:w-6 md:h-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Artist Info */}
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => handleArtistClick(artist.artistId!)}
                      className="text-left w-full"
                    >
                      <h3 className={`font-medium truncate ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                        {artist.displayName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {artistPieces.length} {artistPieces.length === 1 ? 'piece' : 'pieces'}
                      </p>
                    </button>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => handleArtistInfoClick(artist.artistId!, e)}
                      className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
                        showingInfo ? 'bg-gray-200 text-primary-600' : 'text-gray-400'
                      }`}
                      title="Artist Info"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleArtistClick(artist.artistId!)}
                      className="p-1"
                    >
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          isSelected ? 'rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Artist Info */}
              <ArtistInfo artist={artist} isVisible={showingInfo} />

              {/* Pieces Display */}
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-gray-50"
                >
                  {viewMode === 'grid' ? (
                    /* Grid View - Visual focus, no titles, with favorites */
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {artistPieces.map((piece, index) => {
                        const shouldAnimate = animatePieces === artist.artistId;
                        const shouldStagger = shouldStaggerPieceAnimation(shouldAnimate, index);
                        return (
                          <motion.div
                            key={piece.id}
                            initial={shouldStagger ? { opacity: 0, scale: 0.8 } : { opacity: 1, scale: 1 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={getPieceAnimationTransition(shouldAnimate, index)}
                            data-sidebar-anchor={piece.id}
                          >
                            <PieceThumbnail
                              piece={piece}
                              onClick={() => handlePieceClick(piece.id!, getGalleryTileFallbackPath(piece))}
                              className="aspect-square bg-gray-200 rounded overflow-hidden"
                              showTitle={false}
                            />
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    /* List View - Traditional list with titles and favorites */
                    <div>
                      {artistPieces.map((piece, index) => {
                        const shouldAnimate = animatePieces === artist.artistId;
                        const shouldStagger = shouldStaggerPieceAnimation(shouldAnimate, index);
                        return (
                          <motion.div 
                            key={piece.id}
                            className="w-full p-3 pl-8 hover:bg-gray-100 transition-colors"
                            initial={shouldStagger ? { opacity: 0, x: -20 } : { opacity: 1, x: 0 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={getPieceAnimationTransition(shouldAnimate, index)}
                            data-sidebar-anchor={piece.id}
                          >
                          <div className="flex items-center space-x-3">
                            <PieceThumbnail
                              piece={piece}
                              onClick={() => handlePieceClick(piece.id!, getGalleryTileFallbackPath(piece))}
                              className="w-12 h-12 bg-gray-200 rounded flex-shrink-0 overflow-hidden"
                              showTitle={false}
                            />
                            <div className="flex-1 min-w-0">
                              {!hideTitles && (
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {piece.title}
                                </p>
                              )}
                              {piece.date && (
                                <p className={`text-xs text-gray-500 ${hideTitles ? '' : 'mt-1'}`}>
                                  {piece.date}
                                </p>
                              )}
                            </div>
                          </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          );
        })}
        
        {/* Load More Button */}
        {hasMoreArtists && (
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={loadMoreArtists}
              className="w-full py-2 px-4 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Load More Artists ({sidebarArtists.length - visibleCount} remaining)
            </button>
          </div>
        )}
        </div>
      )}
    </div>
  );
}
